import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import React, { useContext } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { UserContext } from 'venn-components';
import {
  analysisViewIdState,
  blockDateRangeInputState,
  blockSettings,
  dateRangeInputConsistencyState,
  dateRangeInputDateRangeState,
  dateRangeInputNameState,
  dateRangeInputsState,
  DEFAULT_CONSISTENCY,
  getNewDateRangeInputId,
  isReportState,
  nextDateRangeInputNameState,
  type DateRangeInputId,
} from 'venn-state';
import { analyticsService, hasDisabledDateRange, useHasFF } from 'venn-utils';
import { DateRangeInputRow } from './DateRangeInputRow';
import InputTitle from './InputTitle';
import { PanelHeader } from './PanelHeader';
import { useSingleInputSelection } from './useSingleInputSelection';

const DateRangesSection = React.memo(function DateRangesSectionInternal() {
  const hasBlockAssignment = useHasFF('block_assignment_ff');
  const { hasPermission } = useContext(UserContext);
  const readonly = !hasPermission('STUDIO_EDIT') || !hasPermission('STUDIO_EDIT_DATES');
  const isReport = useRecoilValue(isReportState);
  const viewId = useRecoilValue(analysisViewIdState);
  const dateRangeIds = useRecoilValue(dateRangeInputsState);

  const isDateRangeDisabled = useRecoilCallback(
    ({ snapshot }) =>
      (blockId: string) => {
        const blockSetting = snapshot.getLoadable(blockSettings(blockId)).valueOrThrow();
        return hasDisabledDateRange(blockSetting.customBlockType);
      },
    [],
  );

  const onAddNew = useRecoilCallback(
    ({ set, snapshot }) =>
      () => {
        const newId = getNewDateRangeInputId();
        const newNameLoadable = snapshot.getLoadable(nextDateRangeInputNameState).valueOrThrow();

        analyticsService.inputsDateRangeGroupCreated({
          name: newNameLoadable,
          consistency: DEFAULT_CONSISTENCY,
          range: 'FULL_HISTORY',
          sourcePage: isReport ? 'REPORT_LAB' : 'STUDIO',
          viewId,
        });

        // TODO(collin.irwin): IMV2 extract this callback to date range input venn-state file so it can be used in multiple places
        // and is more likely to stay up to date
        set(dateRangeInputNameState(newId), newNameLoadable);
        set(dateRangeInputConsistencyState(newId), DEFAULT_CONSISTENCY);
        set(dateRangeInputDateRangeState(newId), { period: 'full' });
        set(dateRangeInputsState, (existingIds) => [...existingIds, newId]);
      },
    [isReport, viewId],
  );

  const { inputsState, selectedInput, onInputSelect, disabled } = useSingleInputSelection(
    dateRangeInputsState,
    blockDateRangeInputState,
    isDateRangeDisabled,
  );

  const handleRadioChange = (value: DateRangeInputId) => {
    onInputSelect(value);
  };

  return hasBlockAssignment ? (
    <>
      <PanelHeader.Root>
        <PanelHeader.Text>Date Ranges</PanelHeader.Text>
        <PanelHeader.NewButton title="Date Ranges" onAddNew={onAddNew} numberOfInputs={dateRangeIds.length} />
      </PanelHeader.Root>
      <RadioGroup
        value={selectedInput}
        onValueChange={handleRadioChange}
        className="flex flex-col gap-0 divide-y divide-x-0 divide-solid divide-venn-grey-30"
        disabled={readonly || disabled}
      >
        {inputsState.map(({ state, id }) => (
          <div key={id} className="flex items-start gap-[4px] px-[12px] py-[8px] last:mb-[24px] hover:bg-venn-grey-10">
            <div className="h-[19px] flex items-center">
              <RadioGroupItem value={id} id={`date-range-${id}`} state={state} disabled={readonly || disabled} />
            </div>
            <Label htmlFor={`date-range-${id}`} className="flex-grow cursor-pointer">
              <DateRangeInputRow
                inputId={id}
                readonly={readonly}
                isPrivate={false}
                deletable={dateRangeIds.length > 1}
              />
            </Label>
          </div>
        ))}
      </RadioGroup>
    </>
  ) : (
    <>
      <InputTitle title="Date Ranges" onAddNew={onAddNew} numberOfInputs={dateRangeIds.length} />
      {dateRangeIds.map((id) => (
        <DateRangeInputRow
          key={id}
          inputId={id}
          readonly={readonly}
          isPrivate={false}
          deletable={dateRangeIds.length > 1}
        />
      ))}
    </>
  );
});

export default DateRangesSection;
